import type { SoundId } from '@audigoal/core';
// Type-only: erased at compile time, so smplr still loads dynamically and stays
// out of the initial bundle.
import type * as SmplrModule from 'smplr';
import { midiToHz } from './timing';
import { PercussionSynth } from './ui-synth';

/**
 * Sampled instruments over `smplr`, with a synthesised fallback voice so the
 * app keeps making sound when samples have not been downloaded (fresh dev
 * checkout, offline first run) or a fetch fails.
 *
 * Samples are self-hosted under `/samples/` and described by a manifest the
 * `samples:fetch` script writes; see docs/plan/10-audio-playback.md.
 */

export interface SampleManifestEntry {
  /** MIDI.js soundfont instrument name, e.g. `acoustic_grand_piano`. */
  instrument: string;
  /** URL of the `-mp3.js` instrument file relative to the site root. */
  url: string;
  license: string;
  source: string;
}

export interface SampleManifest {
  version: number;
  kit: string;
  instruments: Partial<Record<SoundId, SampleManifestEntry>>;
}

export interface PlayNoteOptions {
  /** Absolute audio time; defaults to now. */
  time?: number;
  /** Seconds. */
  duration?: number;
  /** 0..1 */
  velocity?: number;
  /**
   * Cents to bend the note by, for anything that lives between two semitones:
   * a string a little flat, a just third against an equal-tempered one, two
   * pitches beating. MIDI numbers are whole, so this is the only way to say it.
   */
  detuneCents?: number;
}

export type StopVoice = (time?: number) => void;

/** The subset of a smplr instrument we rely on. */
interface LoadedInstrument {
  ready: Promise<void>;
  start(event: {
    note: number;
    velocity?: number;
    time?: number;
    duration?: number;
  }): (time?: number) => void;
  stop(target?: { time?: number }): void;
  dispose(): void;
}

export interface SamplerOptions {
  manifestUrl?: string;
  /** Fetch implementation, injectable for tests. */
  fetchImpl?: typeof fetch;
  /** Loader for the smplr module, injectable for tests. */
  loadSmplr?: () => Promise<typeof SmplrModule>;
  /** Force the synthesised voices (tests, low-memory devices). */
  synthOnly?: boolean;
}

export type SoundSource = 'samples' | 'synth' | 'unloaded';

export class Sampler {
  private manifest: SampleManifest | null | undefined;
  private manifestPromise: Promise<SampleManifest | null> | null = null;
  private instruments = new Map<SoundId, LoadedInstrument>();
  private loading = new Map<SoundId, Promise<void>>();
  private sources = new Map<SoundId, SoundSource>();
  private readonly percussion: PercussionSynth;
  private readonly progress = new Map<SoundId, number>();
  private progressListeners = new Set<(sound: SoundId, ratio: number) => void>();

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly destination: AudioNode,
    private readonly options: SamplerOptions = {},
  ) {
    this.percussion = new PercussionSynth(ctx, destination);
  }

  sourceOf(sound: SoundId): SoundSource {
    return this.sources.get(sound) ?? 'unloaded';
  }

  isLoaded(sound: SoundId): boolean {
    return this.sources.has(sound);
  }

  onProgress(listener: (sound: SoundId, ratio: number) => void): () => void {
    this.progressListeners.add(listener);
    return () => this.progressListeners.delete(listener);
  }

  /** Loads a sound; resolves to the source that will be used. Never rejects. */
  async load(sound: SoundId): Promise<SoundSource> {
    if (this.sources.has(sound)) return this.sources.get(sound) as SoundSource;
    const pending = this.loading.get(sound);
    if (pending !== undefined) {
      await pending;
      return this.sources.get(sound) ?? 'synth';
    }
    const task = this.loadInner(sound);
    this.loading.set(sound, task);
    await task;
    this.loading.delete(sound);
    return this.sources.get(sound) ?? 'synth';
  }

  private async loadInner(sound: SoundId): Promise<void> {
    if (sound === 'click' || sound === 'drums' || this.options.synthOnly === true) {
      this.sources.set(sound, 'synth');
      return;
    }
    try {
      const manifest = await this.getManifest();
      const entry = manifest?.instruments[sound];
      if (entry === undefined) {
        this.sources.set(sound, 'synth');
        return;
      }
      const smplr = await (this.options.loadSmplr ?? (() => import('smplr')))();
      const instrument = smplr.Soundfont(this.ctx, {
        kit: manifest?.kit ?? 'FluidR3_GM',
        instrument: entry.instrument,
        instrumentUrl: entry.url,
        destination: this.destination,
        onLoadProgress: (p) => {
          const ratio = p.total > 0 ? p.loaded / p.total : 0;
          this.progress.set(sound, ratio);
          for (const l of this.progressListeners) l(sound, ratio);
        },
      }) as unknown as LoadedInstrument;
      await instrument.ready;
      this.instruments.set(sound, instrument);
      this.sources.set(sound, 'samples');
    } catch {
      this.sources.set(sound, 'synth');
    }
  }

  private getManifest(): Promise<SampleManifest | null> {
    if (this.manifest !== undefined) return Promise.resolve(this.manifest);
    if (this.manifestPromise !== null) return this.manifestPromise;
    const url = this.options.manifestUrl ?? '/samples/manifest.json';
    const fetchImpl = this.options.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
    if (fetchImpl === undefined) {
      this.manifest = null;
      return Promise.resolve(null);
    }
    this.manifestPromise = fetchImpl(url)
      .then(async (res) => (res.ok ? ((await res.json()) as SampleManifest) : null))
      .catch(() => null)
      .then((m) => {
        this.manifest = m;
        return m;
      });
    return this.manifestPromise;
  }

  /**
   * Plays one note. Sounds not yet loaded use the synth immediately and start
   * loading in the background, so the first tap is never silent.
   */
  play(sound: SoundId, midi: number, options: PlayNoteOptions = {}): StopVoice {
    const time = options.time ?? this.ctx.currentTime;
    const velocity = options.velocity ?? 0.8;
    const duration = options.duration ?? 1;
    if (sound === 'drums') {
      this.percussion.drum(midi, time, velocity);
      return () => {};
    }
    if (sound === 'click') {
      this.percussion.click(time, midi >= 72 ? 'accent' : 'normal');
      return () => {};
    }
    const detune = options.detuneCents ?? 0;
    const instrument = this.instruments.get(sound);
    if (instrument !== undefined) {
      return instrument.start({
        note: midi,
        velocity: Math.round(velocity * 127),
        time,
        duration,
        // smplr takes cents directly.
        ...(detune !== 0 ? { detune } : {}),
      });
    }
    if (!this.sources.has(sound) && !this.loading.has(sound)) void this.load(sound);
    return this.synthVoice(sound, midi, time, duration, velocity, detune);
  }

  stopAll(): void {
    for (const instrument of this.instruments.values()) instrument.stop();
  }

  dispose(): void {
    for (const instrument of this.instruments.values()) instrument.dispose();
    this.instruments.clear();
    this.sources.clear();
  }

  /** A plain but musical stand-in voice per instrument family. */
  private synthVoice(
    sound: SoundId,
    midi: number,
    time: number,
    duration: number,
    velocity: number,
    detuneCents = 0,
  ): StopVoice {
    const design = SYNTH_DESIGNS[sound] ?? SYNTH_DESIGNS.piano;
    const osc = this.ctx.createOscillator();
    osc.type = design.type;
    // A fractional MIDI number is a real frequency; `midiToHz` does not care
    // that the input is not whole.
    osc.frequency.setValueAtTime(midiToHz(midi + detuneCents / 100), time);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(
      Math.min(this.ctx.sampleRate / 2 - 100, design.lowpassHz * (0.5 + velocity)),
      time,
    );
    const gain = this.ctx.createGain();
    const peak = 0.001 + velocity * design.gain;
    const release = design.release;
    const end = time + Math.max(0.05, duration);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peak, time + design.attack);
    if (design.decayTo < 1) {
      gain.gain.exponentialRampToValueAtTime(
        peak * design.decayTo,
        time + design.attack + design.decay,
      );
    }
    gain.gain.setValueAtTime(
      peak * design.decayTo,
      Math.max(end, time + design.attack + design.decay),
    );
    gain.gain.exponentialRampToValueAtTime(0.0001, end + release);
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.destination);
    osc.start(time);
    osc.stop(end + release + 0.05);
    if (design.detuneCents !== 0) {
      const osc2 = this.ctx.createOscillator();
      osc2.type = design.type;
      osc2.frequency.setValueAtTime(midiToHz(midi), time);
      osc2.detune.setValueAtTime(design.detuneCents, time);
      const g2 = this.ctx.createGain();
      g2.gain.setValueAtTime(0.35, time);
      osc2.connect(g2);
      g2.connect(filter);
      osc2.start(time);
      osc2.stop(end + release + 0.05);
    }
    return (stopAt?: number) => {
      const t = stopAt ?? this.ctx.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
      osc.stop(t + 0.1);
    };
  }
}

interface SynthDesign {
  type: OscillatorType;
  lowpassHz: number;
  attack: number;
  decay: number;
  decayTo: number;
  release: number;
  gain: number;
  detuneCents: number;
}

const SYNTH_DESIGNS: Record<SoundId, SynthDesign> = {
  piano: {
    type: 'triangle',
    lowpassHz: 3200,
    attack: 0.005,
    decay: 0.6,
    decayTo: 0.35,
    release: 0.25,
    gain: 0.5,
    detuneCents: 0,
  },
  ukulele: {
    type: 'triangle',
    lowpassHz: 4500,
    attack: 0.003,
    decay: 0.35,
    decayTo: 0.25,
    release: 0.15,
    gain: 0.45,
    detuneCents: 0,
  },
  'guitar-nylon': {
    type: 'triangle',
    lowpassHz: 2800,
    attack: 0.004,
    decay: 0.5,
    decayTo: 0.3,
    release: 0.2,
    gain: 0.45,
    detuneCents: 0,
  },
  // A sawtooth here read as a twangy toy rather than a steel string: the odd
  // harmonics sit right where a guitar has none. A triangle with a little
  // detune and a brighter filter is closer, and quieter into the bargain. It
  // is still a stand-in — a steel guitar is what `pnpm samples:fetch` is for.
  'guitar-steel': {
    type: 'triangle',
    lowpassHz: 3400,
    attack: 0.004,
    decay: 0.55,
    decayTo: 0.28,
    release: 0.2,
    gain: 0.4,
    detuneCents: 4,
  },
  ocarina: {
    type: 'sine',
    lowpassHz: 6000,
    attack: 0.04,
    decay: 0.2,
    decayTo: 0.9,
    release: 0.12,
    gain: 0.5,
    detuneCents: 0,
  },
  bass: {
    type: 'sawtooth',
    lowpassHz: 700,
    attack: 0.005,
    decay: 0.4,
    decayTo: 0.5,
    release: 0.12,
    gain: 0.35,
    detuneCents: 0,
  },
  strings: {
    type: 'sawtooth',
    lowpassHz: 1800,
    attack: 0.15,
    decay: 0.3,
    decayTo: 0.9,
    release: 0.4,
    gain: 0.18,
    detuneCents: 7,
  },
  choir: {
    type: 'triangle',
    lowpassHz: 1500,
    attack: 0.2,
    decay: 0.3,
    decayTo: 0.9,
    release: 0.4,
    gain: 0.22,
    detuneCents: 5,
  },
  click: {
    type: 'sine',
    lowpassHz: 8000,
    attack: 0.001,
    decay: 0.02,
    decayTo: 0.1,
    release: 0.01,
    gain: 0.3,
    detuneCents: 0,
  },
  drums: {
    type: 'sine',
    lowpassHz: 8000,
    attack: 0.001,
    decay: 0.1,
    decayTo: 0.1,
    release: 0.05,
    gain: 0.5,
    detuneCents: 0,
  },
};
